import { useRecoilValue } from "recoil";

import { ISelection } from "src/constants/interfaces/bet";
import { useTranslation } from "src/hooks/translation";
import { useSelectionSubscription } from "src/hooks/selectionSubscription";
import betBuilderStore from "src/stores/betBuilder";
import { emitGoToGame } from "src/services/communicator/actions";
import { GameTypeEnum, GameTypeViewEnum } from "src/constants/enums/gameType";
import MarketBase from "src/common/components/marketBase";

import "./style.scss";

interface IProps {
  gameId: number;
  selections: ISelection[];
  // Remove a single bet builder selection inside this group.
  onRemoveSelection(eventId: number): void;
  // Remove the whole bet builder group (all selections for this game).
  onRemoveGroup(eventIds: number[]): void;
}

interface IItemProps {
  selection: ISelection;
  isLast: boolean;
  onRemove(eventId: number): void;
  removeLabel: string;
  inactiveLabel: string;
}

/**
 * Single BB leg row. Lives in its own component so we can call
 * `useSelectionSubscription` per leg (hooks can't run inside a `.map`).
 *
 * Without this subscription a BB leg's `selection.event.price` is whatever
 * was on the event at click time — and never updates with the live swarm
 * feed. The visible effect: after "Accept Odd Changes" the slip places the
 * bet with the now-stale leg price, the server replies "odds have been
 * changed" again, and the user is stuck in a loop. Subscribing keeps each
 * leg's price live so the next place-bet sends what the server expects.
 */
const BetBuilderGroupItem = ({
  selection,
  isLast,
  onRemove,
  removeLabel,
  inactiveLabel,
}: IItemProps) => {
  // Keeps `selection.event.price` in sync with the swarm event feed.
  // Discard the returned loading flag — BB leg loading is already
  // surfaced through the card's combined-odd spinner.
  useSelectionSubscription(selection);

  const isInactive = !!selection.inactive;
  const itemClass = [
    "B-bet-builder-group-item",
    isLast ? "is-last" : "",
    isInactive ? "is-inactive" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={itemClass}>
      <span className="B-bet-builder-group-item-dot" />
      <span className="B-bet-builder-group-item-market">
        {selection.market.name}
      </span>
      <span className="B-bet-builder-group-item-pill">
        <span className="B-bet-builder-group-item-pill-event">
          {selection.event.name}
          <MarketBase base={selection.event.base} />
        </span>
      </span>
      {isInactive && (
        <span
          className="B-bet-builder-group-item-deleted"
          title={inactiveLabel}
        >
          <i className="global-icon icon-blocked" />
          <span>{inactiveLabel}</span>
        </span>
      )}
      <span className="B-bet-builder-group-item-spacer" />
      <button
        type="button"
        className="global-icon icon-Close B-bet-builder-group-item-close"
        onClick={() => onRemove(selection.event.id)}
        aria-label={removeLabel}
      />
    </div>
  );
};

// Bet builder card matching Figma design:
// - Game header row (sport icon, team names, close X)
// - BB badge + "BET BUILDER" + combined odd in one subheader row
// - Each selection: yellow dot + market name + yellow odd pill + close X
// - Vertical dotted line connecting yellow dots
const BetBuilderGroup = ({
  gameId,
  selections,
  onRemoveSelection,
  onRemoveGroup,
}: IProps) => {
  const t = useTranslation();
  const state = useRecoilValue(betBuilderStore);
  const group = state.groups[gameId];

  if (!selections.length) return null;

  const { game, market, event } = selections[0];
  const loading = !!group?.loading;
  const combinedPrice = group?.combinedPrice;
  const sportAlias = game.sport_alias || game.sport?.alias || "";
  const regionAlias = game.region_alias || game.region?.alias || event.region?.alias || "";

  // Opens the game details view with the bet builder toggle already on, same
  // navigation path a regular selection's match-name click uses.
  const goToBetBuilderGame = () => {
    if (game.is_blocked) return;
    emitGoToGame({
      gameId: game.id,
      sportAlias,
      regionAlias,
      sportId: event.sport?.id || game.sportId,
      regionId: game.regionId || event.region?.id,
      competitionId: game.competition?.id || game.competitionId || event.competition?.id,
      marketId: Object.keys(game.market || {})[0],
      listType: "all",
      gameType: GameTypeViewEnum[game.is_live ? GameTypeEnum.Live : GameTypeEnum.Prematch],
      betBuilder: true,
    });
  };

  return (
    <div className="B-bet-builder-group">
      {/* Game header row — clickable to open game details with BB toggle on */}
      <div className="B-bet-builder-group-gamerow">
        <div className="B-bet-builder-group-game" onClick={goToBetBuilderGame}>
          {sportAlias && (
            <span className={`B-sport-icon sport-icon sport-icon-${sportAlias}`} />
          )}
          <span className="B-bet-builder-group-gametext">
            {game.team1_name}
            {game.team2_name && (
              <>
                <span className="B-bet-builder-group-gamedash"> - </span>
                {game.team2_name}
              </>
            )}
          </span>
        </div>
        <button
          type="button"
          className="global-icon icon-Close B-bet-builder-group-close"
          onClick={() => onRemoveGroup(selections.map((s) => s.event.id))}
          aria-label={t("Remove bet builder")}
        />
      </div>

      {/* Bet builder subheader: BB badge + label + combined odd */}
      <div className="B-bet-builder-group-subheader">
        <div className="B-bet-builder-group-subheader-left">
          <span className="B-bet-builder-badge">{t("BB")}</span>
          <span className="B-bet-builder-title">{t("Bet Builder")}</span>
        </div>
        <div className="B-bet-builder-group-subheader-right">
          <span className="B-bet-builder-combined-odd">
            {loading ? "…" : combinedPrice ? combinedPrice.toFixed(2) : "-"}
          </span>
        </div>
      </div>

      {/* Selections list with yellow dots + connecting line.
          Legs flagged `inactive` (rejected by the post-place-bet
          get_bet_builder_odds refresh) get a red "deleted event" treatment
          + the standard inactive icon — the user can still click the close
          button to remove them from the slip. Each leg is rendered through
          BetBuilderGroupItem so we can subscribe its event to swarm and
          keep the leg's price live. */}
      <div className="B-bet-builder-group-items">
        {selections.map((selection, idx) => (
          <BetBuilderGroupItem
            key={selection.event.id}
            selection={selection}
            isLast={idx === selections.length - 1}
            onRemove={onRemoveSelection}
            removeLabel={t("Remove selection")}
            inactiveLabel={t("Event Is Inactive")}
          />
        ))}
      </div>
    </div>
  );
};

export default BetBuilderGroup;
